import toast from 'react-hot-toast';
import { sendMessage, StreamingResponse, ApiMessage, generateSuggestedTopics } from './api';
import { Message } from './message';
import { SuggestedTopic } from './suggested_topic';

export class ConversationThread {
  private id: string = Date.now().toString();
  private items: (Message | ConversationThread)[] = [];
  private suggestedTopics: SuggestedTopic[] = [];

  private onToken: ((token: string) => void) | undefined;
  private onComplete: ((assistantMessage: Message) => void) | undefined;
  private onError: ((error: Error) => void) | undefined;
  private onSuggestedTopicsChanged: (() => void) | undefined;

  /**
   * Get the unique identifier for this conversation.
   */
  getId(): string {
    return this.id;
  }

  /**
   * Get all items in the conversation
   */
  getAllItems(): (Message | ConversationThread)[] {
    return [...this.items];
  }

  /**
   * Add a message to the conversation
   */
  private addMessage(message: Message): void {
    this.items.push(message);
  }

  /**
   * Add a child thread to the conversation
   */
  addChildThread(thread: ConversationThread): void {
    this.items.push(thread);
  }

  getInitialUserQuery(): string | undefined {
    const firstMessage = this.items.at(0);
    if (firstMessage && 'content' in firstMessage) {
      return firstMessage.content;
    }
    return undefined;
  }

  /**
   * Send a user query and handle the streaming response
   */
  async sendUserQuery(content: string): Promise<void> {
    // Create and add user message
    const userMessage: Message = {
      id: this.generateMessageId(),
      role: 'user',
      content,
      timestamp: new Date()
    };
    this.addMessage(userMessage);

    // Refresh suggested topics asynchronously after message completion
    this.refreshSuggestedTopics().catch(error => {
      console.warn('Failed to refresh topics after message completion:', error);
    });

    // Prepare assistant message
    let assistantContent = '';
    const assistantMessage: Message = {
      id: this.generateMessageId(),
      role: 'assistant',
      content: '',
      timestamp: new Date()
    };

    const callbacks: StreamingResponse = {
      onToken: (token: string) => {
        assistantContent += token;
        assistantMessage.content = assistantContent;
        this.onToken?.(token);
      },
      onComplete: () => {
        assistantMessage.content = assistantContent;
        this.addMessage(assistantMessage);
        this.onComplete?.(assistantMessage);
      },
      onError: (error: Error) => {
        this.onError?.(error);
      }
    };

    // Convert conversation history to API format (only Message objects)
    // TODO: Consider if we want to store summaries of a thread, and inject that into 
    // the parent thread as context for the LLM.
    const apiMessages: ApiMessage[] = this.items
      .filter((item): item is Message => 'content' in item)
      .map(msg => ({
        role: msg.role,
        content: msg.content
      }));

    try {
      await sendMessage(apiMessages, callbacks);
    } catch (error) {
      this.onError?.(error as Error);
    }
  }

  async retryUserQuery(): Promise<void> {
    // Prepare assistant message
    let assistantContent = '';
    const assistantMessage: Message = {
      id: this.generateMessageId(),
      role: 'assistant',
      content: '',
      timestamp: new Date()
    };

    const callbacks: StreamingResponse = {
      onToken: (token: string) => {
        assistantContent += token;
        assistantMessage.content = assistantContent;
        this.onToken?.(token);
      },
      onComplete: () => {
        assistantMessage.content = assistantContent;
        this.addMessage(assistantMessage);
        this.onComplete?.(assistantMessage);
      },
      onError: (error: Error) => {
        this.onError?.(error);
      }
    };

    // Convert conversation history to API format (only Message objects)
    // TODO: Consider if we want to store summaries of a thread, and inject that into 
    // the parent thread as context for the LLM.
    const apiMessages: ApiMessage[] = this.items
      .filter((item): item is Message => 'content' in item)
      .map(msg => ({
        role: msg.role,
        content: msg.content
      }));

    try {
      await sendMessage(apiMessages, callbacks);
    } catch (error) {
      this.onError?.(error as Error);
    }
  }

  /**
   * Clear all messages from the conversation
   */
  clearAllItems(): void {
    this.items = [];
  }

  /**
   * Get the number of messages in the conversation
   */
  getMessageCount(): number {
    return this.items.length;
  }

  /**
   * Get only the Message objects from the conversation
   */
  getMessagesOnly(): Message[] {
    return this.items.filter((item): item is Message => 'content' in item);
  }

  /**
   * Get only the ConversationThread objects from the conversation
   */
  getChildThreads(): ConversationThread[] {
    return this.items.filter((item): item is ConversationThread => item instanceof ConversationThread);
  }

  /**
   * Get the number of Message objects only
   */
  getMessageOnlyCount(): number {
    return this.getMessagesOnly().length;
  }

  /**
   * Get the number of child threads
   */
  getChildThreadCount(): number {
    return this.getChildThreads().length;
  }

  registerOnToken(onToken: (token: string) => void) {
    this.onToken = onToken;
  }

  unregisterOnToken() {
    this.onToken = undefined;
  }

  registerOnComplete(onComplete: (assistantMessage: Message) => void) {
    this.onComplete = onComplete;
  }

  unregisterOnComplete() {
    this.onComplete = undefined;
  }

  registerOnError(onError: (error: Error) => void) {
    this.onError = onError;
  }

  unregisterOnError() {
    this.onError = undefined;
  }

  registerOnSuggestedTopicsChanged(onSuggestedTopicsChanged: () => void) {
    this.onSuggestedTopicsChanged = onSuggestedTopicsChanged;
  }

  unregisterOnSuggestedTopicsChanged() {
    this.onSuggestedTopicsChanged = undefined;
  }

  /**
   * Get the current suggested topics for this conversation
   */
  getSuggestedTopics(): SuggestedTopic[] {
    return [...this.suggestedTopics];
  }

  /**
   * Update the suggested topics for this conversation
   */
  private setSuggestedTopics(topics: SuggestedTopic[]): void {
    this.suggestedTopics = topics;
    this.onSuggestedTopicsChanged?.();
  }

  /**
   * Generate suggested topics based on conversation context
   */
  private async generateSuggestedTopics(): Promise<void> {
    // For now, return mock topics - this would be replaced with actual API call
    const messages = this.getMessagesOnly();
    if (messages.length === 0) {
      return;
    }

    let assistantContent = '';

    generateSuggestedTopics(messages, {
      onToken: (token: string) => {
        assistantContent += token;
      },
      onComplete: () => {
        try {
          const suggestedTopics = JSON.parse('[{' + assistantContent)
          this.setSuggestedTopics(suggestedTopics);
        } catch (e) {
          toast.error('Failed to parse suggested topics response');
        }
      },
      onError: (error: Error) => toast.error(`Failed to generate suggested topics: ${error.message}`)
    });

  }

  /**
   * Refresh suggested topics asynchronously
   */
  private async refreshSuggestedTopics(): Promise<void> {
    try {
      await this.generateSuggestedTopics();
    } catch (error) {
      console.warn('Failed to refresh suggested topics:', error);
    }
  }

  /**
   * Generate a unique message ID
   */
  private generateMessageId(): string {
    return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}